"use client";

import { useEffect, useState, useCallback } from "react";
import ActionButton from "../molecules/ActionButton";
import CallPopup from "@/app/components/callBack";
import { useCallCheck } from "@/app/hooks/useCallCheck";
import { usePathname } from "next/navigation";
import PreCommandePopup from "@/app/components/CommandePopup";
import { idelioService } from "@/app/services/idelioService";

export default function ActionSidebar() {
  const [showPopup, setShowPopup] = useState(false);
  const { isAllowed } = useCallCheck();
  const pathname = usePathname();

  const [showPreCommandePopup, setShowPreCommandePopup] = useState(false);
  const [hasContactAnchor, setHasContactAnchor] = useState(false);

  // Initialisé à false par défaut
  const [idelioStatus, setIdelioStatus] = useState<boolean>(false);

  const isAgencyPage = pathname.startsWith("/portage-de-repas/");
  const pathParts = pathname.split("/").filter(Boolean);
  const agencySlug = isAgencyPage ? pathParts[1] : null;

  const fetchStatus = useCallback(async (slug: string) => {
    try {
      const agenciesEndpoint = process.env.NEXT_PUBLIC_AGENCIES_ENDPOINT;
      if (!agenciesEndpoint) return;

      const url = new URL(agenciesEndpoint);
      url.searchParams.set("filters[APIBackend][Slug][$eq]", slug);
      url.searchParams.set("populate[APIBackend]", "true");

      const res = await fetch(url.href);
      const json = await res.json();
      const agence = json.data?.[0];

      if (agence?.uuid) {
        // La méthode getStatusIdelio met aussi à jour le localStorage en arrière-plan
        const status = await idelioService.getStatusIdelio(String(agence.uuid));
        setIdelioStatus(typeof status === "boolean" ? status : Boolean(status?.active ?? status));
      } else {
        setIdelioStatus(false);
      }
    } catch (error) {
      console.error("Erreur chargement statut Idelio dans Sidebar :", error);
      setIdelioStatus(false);
    }
  }, []);

  useEffect(() => {
    if (isAgencyPage) {
      if (agencySlug) {
        fetchStatus(agencySlug);
      } else {
        setIdelioStatus(false);
      }
    } else {
      // Si ce n'est pas une page agence, on récupère le statut depuis le localStorage
      try {
        const storedAgency = localStorage.getItem("agencyIdelio");
        if (storedAgency) {
          const parsed = JSON.parse(storedAgency);
          // On vérifie la propriété showIdelioButton stockée dans l'objet
          setIdelioStatus(Boolean(parsed.showIdelioButton));
        } else {
          setIdelioStatus(false);
        }
      } catch (err) {
        console.error("Erreur lecture localStorage agencyIdelio :", err);
        setIdelioStatus(false);
      }
    }
  }, [isAgencyPage, agencySlug, fetchStatus]);

  const isCallActive = idelioStatus;
  
  const contactHref = isAgencyPage && agencySlug
    ? `/portage-de-repas/${agencySlug}/contact`
    : "#contact";

  const showContactButton = isAgencyPage || hasContactAnchor;

  useEffect(() => {
    if (isAgencyPage) {
      setHasContactAnchor(false);
      return;
    }

    const checkContactAnchor = () => {
      const contactElement = document.getElementById("contact");
      setHasContactAnchor(!!contactElement);
    };

    const raf = requestAnimationFrame(checkContactAnchor);
    return () => cancelAnimationFrame(raf);
  }, [pathname, isAgencyPage]);

  return (
    <>
      {/* Version desktop */}
      <aside
        aria-label="Actions rapides"
        className="fixed right-3 md:right-2 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col items-center gap-4"
      >
        {isCallActive && (
          <ActionButton
            iconName="https://site-lms.fra1.digitaloceanspaces.com/phone_d3bb0f75b3.svg"
            label="Appel immédiat"
            onclick={() => setShowPopup(true)}
          />
        )}

        {showContactButton && (
          <ActionButton
            iconName="https://site-lms.fra1.digitaloceanspaces.com/Mail_46bac8fc9f.svg"
            label="Être contacté"
            href={contactHref}
          />
        )}
         
        <ActionButton
          iconName="https://site-lms.fra1.digitaloceanspaces.com/Mall_5f93dab076.svg"
          label="Commander"
          target="_blank"
          onclick={() => setShowPreCommandePopup(true)}
        />
      </aside>

      {/* Version mobile */}
      <div
        aria-label="Actions rapides"
        className="fixed bottom-0 inset-x-0 z-50 lg:hidden bg-secondary text-white pb-4"
      >
        <div className="flex justify-center items-stretch">
          {isCallActive && (
            <ActionButton
              iconName="https://site-lms.fra1.digitaloceanspaces.com/phone_d3bb0f75b3.svg"
              label="Appel immédiat"
              className="mt-[-20px]"
              onclick={() => setShowPopup(true)}
            />
          )}

          {showContactButton && (
            <ActionButton
              iconName="https://site-lms.fra1.digitaloceanspaces.com/Mail_46bac8fc9f.svg"
              label="Être contacté"
              href={contactHref}
              className="mt-[-20px]"
            />
          )}

          <ActionButton
            iconName="https://site-lms.fra1.digitaloceanspaces.com/Mall_5f93dab076.svg"
            label="Commander"
            target="_blank"
            onclick={() => setShowPreCommandePopup(true)}
            className="mt-[-20px]"
          />
        </div>
      </div>

      {showPopup && <CallPopup onClose={() => setShowPopup(false)} />}
      {showPreCommandePopup && (
        <PreCommandePopup
          onClose={() => setShowPreCommandePopup(false)}
        />
      )}
    </>
  );
}